import React from 'react'
import { useTranslation } from 'react-i18next'

import { BaseView, BaseText } from '@/components/base'
import { AppIcons } from '@/components/AppIcons'
import { Avatar } from '@/components/Avatar'
import { TransactionCurrencyAvatar } from '@/components/TransactionCurrencyAvatar'
import { useTheme } from '@/hooks/useTheme'
import { responsiveAdapter } from '@/utils'
import { Transaction, transactionServices } from '@/domain/transactions'
import { useUserStore } from '@/store/useUserStore'
import { HeaderConvertedProps } from './types'

export const HeaderConverted: React.FC<HeaderConvertedProps> = ({
  transaction,
  isLoading = false,
}) => {
  // All hooks MUST be called before any early returns
  const { t } = useTranslation()
  const theme = useTheme()
  const completeCurrencies = useUserStore(
    (state) => state.wallet?.completeCurrencies,
  )

  // Early return for loading state AFTER all hooks are called
  if (isLoading || !transaction) {
    return (
      <BaseView row align="center" justify="space-between" py={32} px={16}>
        {/* From Currency Skeleton */}
        <BaseView align="center" gap={16}>
          <BaseView
            circle
            circleSize={120}
            style={{
              backgroundColor: theme.colors.background.secondaryGray + '40',
            }}
          />
          <BaseView
            style={{
              width: 100,
              height: 16,
              backgroundColor: theme.colors.background.secondaryGray + '40',
              borderRadius: 4,
            }}
          />
        </BaseView>

        {/* Arrow */}
        <BaseView
          circle
          circleSize={40}
          center
          style={{
            backgroundColor: theme.colors.background.secondaryGray + '40',
          }}
        />

        {/* To Currency Skeleton */}
        <BaseView align="center" gap={16}>
          <BaseView
            circle
            circleSize={120}
            style={{
              backgroundColor: theme.colors.background.secondaryGray + '40',
            }}
          />
          <BaseView
            style={{
              width: 100,
              height: 16,
              backgroundColor: theme.colors.background.secondaryGray + '40',
              borderRadius: 4,
            }}
          />
        </BaseView>
      </BaseView>
    )
  }

  const isPending = transaction.state === 'processing'
  const isFailed = ['declined', 'cancelled'].includes(transaction.state)
  const isRefunded = transaction.state === 'refunded'
  const isFailedOrRefunded = isFailed || isRefunded

  // Format amounts
  const fromAmount = transaction.amount?.amount || '0'
  const fromCurrency = transaction.amount?.currency || 'USDT'
  const toAmount = transaction.subject_swap?.credit_amount?.amount || '0'
  const toCurrency = transaction.subject_swap?.credit_amount?.currency || 'USDT'

  // Find currency details for proper icons and colors
  const fromCurrencyDetails = completeCurrencies?.find(
    (currency) => currency.code === fromCurrency,
  )
  const toCurrencyDetails = completeCurrencies?.find(
    (currency) => currency.code === toCurrency,
  )

  const formatAmount = (amount: string, currency: string) => {
    let formattedAmount = amount

    // Format USDT with 2 decimals
    if (currency === 'USDT') {
      formattedAmount = Number(amount).toFixed(2)
    }

    // Display GOLD instead of XAUT
    const displayCurrency = currency === 'XAUT' ? 'GOLD' : currency

    return `${formattedAmount} ${displayCurrency}`
  }

  const renderCurrencyAvatar = (
    currency: string,
    amount: string,
    currencyDetails: any,
    iconSize: number = 60, // Add iconSize parameter as in RN
  ) => {
    return (
      <BaseView align="center" gap={16}>
        {/* Currency Avatar - as in RN Avatar with isCurrency=true */}
        <TransactionCurrencyAvatar
          uri={currencyDetails?.icon}
          bgColor={currencyDetails?.avatarBGRefColor || '#6B7280'}
          size={120}
          iconSize={iconSize} // Use the passed iconSize
          fallbackText={currency}
          containerGradientProps={{
            locations: undefined,
            start: [1, 0],
            end: [0, 1],
          }}
        />

        {/* Amount */}
        <BaseText
          fontSize={16}
          fontFamily="medium"
          style={{
            color: isFailedOrRefunded
              ? theme.colors.label.disabled
              : theme.colors.label.default,
            textDecorationLine: isFailedOrRefunded ? 'line-through' : 'none',
          }}
          textAlign="center"
        >
          {formatAmount(amount, currency)}
        </BaseText>
      </BaseView>
    )
  }

  return (
    <BaseView gap={24} py={32}>
      {/* Conversion Display */}
      <BaseView
        row
        align="center"
        justify="space-between"
        style={{ width: '100%' }}
      >
        {/* From Currency */}
        <BaseView flex={1} align="center">
          {renderCurrencyAvatar(
            fromCurrency,
            fromAmount,
            fromCurrencyDetails,
            60,
          )}
        </BaseView>

        {/* Arrow */}
        <BaseView
          style={{
            width: 45,
            height: 45,
            borderRadius: 22.5,
            backgroundColor: theme.colors.background.cardItemRoot,
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: 16,
            marginRight: 16,
          }}
        >
          <AppIcons
            name="arrowBigRight"
            size={24}
            color={theme.colors.label.default}
          />
        </BaseView>

        {/* To Currency */}
        <BaseView flex={1} align="center">
          {renderCurrencyAvatar(toCurrency, toAmount, toCurrencyDetails, 70)}
        </BaseView>
      </BaseView>

      {/* Status */}
      {isPending && (
        <BaseView align="center">
          <BaseView
            row
            align="center"
            gap={8}
            py={8}
            px={16}
            style={{
              backgroundColor: theme.colors.background.secondaryGray + '20',
              borderRadius: 20,
            }}
          >
            <AppIcons
              name="clock"
              size={16}
              color={theme.colors.label.description}
            />
            <BaseText
              fontSize={14}
              fontFamily="semibold"
              color="label.description"
            >
              {t('translation:pending') || 'Pending'}
            </BaseText>
          </BaseView>
        </BaseView>
      )}

      {/* Failed/Refunded Status */}
      {isFailedOrRefunded && (
        <BaseView align="center">
          <BaseView
            row
            align="center"
            gap={8}
            py={8}
            px={16}
            style={{
              backgroundColor: theme.colors.background.secondaryGray + '20',
              borderRadius: 20,
            }}
          >
            <AppIcons
              name="triangleWarning"
              size={16}
              color={theme.colors.label.flagLosses}
            />
            <BaseText
              fontSize={14}
              fontFamily="semibold"
              color="label.flagLoss"
            >
              {isFailed ? 'Failed' : 'Refunded'}
            </BaseText>
          </BaseView>
        </BaseView>
      )}
    </BaseView>
  )
}
